import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { ArrowLeft, Send } from 'lucide-react';
import { intervisieApi } from '../services/intervisieApi';
import { toast } from 'react-hot-toast';

export const IntervisieGroepAanmeldenPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();

  const { data: options } = useQuery({
    queryKey: ['intervisie-options'],
    queryFn: intervisieApi.getOptions,
  });

  const [form, setForm] = useState({
    name: '',
    max_members: '8',
    planning: '',
    doelgroep: '',
    postcode: '',
    straatnaam: '',
    huisnummer: '',
    toevoeging: '',
    plaats: '',
    provincie: '',
    description: '',
    is_online: false,
    akkoord_vermelding: false,
  });

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const createMutation = useMutation({
    mutationFn: () =>
      intervisieApi.createGroup({
        ...form,
        max_members: parseInt(form.max_members),
        doelgroep: form.doelgroep || undefined,
        akkoord_vermelding: form.akkoord_vermelding,
      }),
    onSuccess: (group) => {
      toast.success(t('intervisie.groupRegistered'));
      navigate(`/intervisie/${group.id}`);
    },
    onError: (err: any) => {
      const errors = err?.response?.data?.errors;
      if (errors?.length) {
        toast.error(errors[0]);
      } else {
        toast.error(t('intervisie.error'));
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.akkoord_vermelding) {
      toast.error(t('intervisie.agreeTermsRequired'));
      return;
    }
    createMutation.mutate();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/intervisie" className="hover:text-[#0B1F3A] flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> {t('intervisie.title')}
        </Link>
        <span>›</span>
        <span className="text-gray-700">{t('intervisie.registerTitle')}</span>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800">
        {t('intervisie.registerInfo')}
      </div>

      <Card>
        <h1 className="text-xl font-bold text-gray-900 mb-6">{t('intervisie.registerTitle')}</h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.groupName')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder={t('intervisie.groupNamePlaceholder')}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.maxMembers')} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={form.max_members}
              onChange={set('max_members')}
              min={5}
              max={10}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            />
            <p className="text-xs text-gray-400 mt-1">{t('intervisie.maxMembersHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.planning')} <span className="text-red-500">*</span>
            </label>
            <select
              value={form.planning}
              onChange={set('planning')}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            >
              <option value="">{t('intervisie.selectPlanning')}</option>
              {(options?.planning_options || []).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.targetGroupRole')} <span className="text-red-500">*</span>
            </label>
            <select
              value={form.doelgroep}
              onChange={set('doelgroep')}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            >
              <option value="">{t('intervisie.selectRole')}</option>
              {(options?.doelgroep_options || []).map((opt) => (
                <option key={opt.value} value={opt.value}>{t(`screening.types.${opt.value}`, { defaultValue: opt.label })}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">{t('intervisie.targetGroupHint')}</p>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_online}
                onChange={(e) => setForm((prev) => ({ ...prev, is_online: e.target.checked }))}
                className="rounded border-gray-300 text-[#0B1F3A] focus:ring-[#0B1F3A]"
              />
              <span className="text-sm text-gray-700">{t('intervisie.isOnline')}</span>
            </label>
          </div>

          {!form.is_online && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('intervisie.address')}</label>
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">{t('intervisie.postalCode')}</label>
                  <input
                    type="text"
                    value={form.postcode}
                    onChange={set('postcode')}
                    placeholder="1234 AB"
                    maxLength={10}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">{t('intervisie.houseNumber')}</label>
                  <input
                    type="text"
                    value={form.huisnummer}
                    onChange={set('huisnummer')}
                    placeholder="12"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">{t('intervisie.addition')}</label>
                  <input
                    type="text"
                    value={form.toevoeging}
                    onChange={set('toevoeging')}
                    placeholder="A"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-xs text-gray-500 mb-1">{t('intervisie.street')}</label>
                  <input
                    type="text"
                    value={form.straatnaam}
                    onChange={set('straatnaam')}
                    placeholder={t('intervisie.street')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-xs text-gray-500 mb-1">{t('intervisie.city')}</label>
                  <input
                    type="text"
                    value={form.plaats}
                    onChange={set('plaats')}
                    placeholder={t('intervisie.city')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                  />
                </div>
                <div className="col-span-6">
                  <label className="block text-xs text-gray-500 mb-1">{t('intervisie.province')}</label>
                  <select
                    value={form.provincie}
                    onChange={set('provincie')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                  >
                    <option value="">{t('intervisie.selectProvince')}</option>
                    {(options?.provincies || []).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.description')}
            </label>
            <textarea
              value={form.description}
              onChange={set('description')}
              rows={4}
              placeholder={t('intervisie.descriptionPlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] resize-none"
            />
          </div>

          <div className="pt-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.akkoord_vermelding}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, akkoord_vermelding: e.target.checked }))
                }
                className="mt-0.5 rounded border-gray-300 text-[#0B1F3A] focus:ring-[#0B1F3A]"
                required
              />
              <span className="text-sm text-gray-700">
                {t('intervisie.agreeTerms')} <span className="text-red-500">*</span>
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate('/intervisie')}>
              {t('intervisie.cancel')}
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              <Send className="w-4 h-4 mr-2" />
              {t('intervisie.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
